import { UserType } from 'modules/User/user.types';
import { Dispatch, SetStateAction } from 'react';

export interface MapsHeaderProps {
  label: string;
}

export interface MapListButtonProps {
  title: string;
  isAccessAllowed: boolean;
  onClick: (title: string) => void;
  // since the title prop is used as the key for the mapDataIndex,
  // we can use the altTitle prop to display a different title for cases where we want to display a different title than the key
  altTitle?: string;
}

export interface AllMapListProps {
  userType: UserType;
  handleAddWidget: (config: MapMenuButtonConfig, buttonType: MapCreationType) => void;
  onClickUpgrade: () => void;
}

export interface SelectionGridProps {
  onClick: (config: MapMenuButtonConfig, buttonType: MapCreationType) => void;
  onClickUpgrade: () => void;
  mapCreationType: MapCreationType;
  chosenTerm?: string;
}

export interface MapMenuButtonConfig {
  label: string; // Used as `region` property in Mixpanel tracking, translate with caution!
  icon: string; // For other maps, defaults to <WorldIcon />
  permission: UserType; // Other than 2 free maps, all defaults to premium
  shouldShowAsDefaults: boolean; // Only 8 maps should show up by default
  importMapUrl: string;
}

// The DefaultMapConfig refers to the 8 default maps that requires
// special handling like new names, special icons and different permissions.
export type DefaultMapConfig = {
  originalName: string;
  newName?: string;
  newIcon?: string; // For other maps, defaults to <WorldIcon />
  newPermission?: UserType; // Other than 2 free maps, all defaults to premium
};

export interface MapButtonProps {
  menuButtonConfig: MapMenuButtonConfig;
  onClick: (config: MapMenuButtonConfig, buttonType: MapCreationType) => void;
  permission: UserType;
  isAccessAllowed: boolean;
  buttonType: MapCreationType;
}

export interface Geometry {
  type: string;
  arcs?: (number[] | number)[][] | number[] | undefined;
  id?: string | undefined;
  properties: {
    name?: string | null | number;
    'hc-key'?: string;
  };
}

export enum MapType {
  Choropleth = 'Choropleth',
  Bubble = 'Bubble',
  Monochrome = 'Monochrome',
}

// Matches the MAP_CREATED.from values in mixpanel.ts constants
export enum MapCreationType {
  Popular = 'Popular', // Directly clicking the maps in popular maps
  Search = 'Search', // Chooses 1 of the searched maps
  Duplication = 'Duplication', // Duplicating existing maps
  List = 'List', // Clicking on the list of maps
}

export type MapWidgetSearchInputProps = {
  chosenTerm: string;
  searchKeyword: string;
  setSearchKeyword: Dispatch<SetStateAction<string>>;
  setChosenTerm: Dispatch<SetStateAction<string>>;
};

export type ImportedMap = {
  arcs?: ArcArray;
  bbox?: number[];
  copyright?: string;
  copyrightShort?: string;
  copyrightUrl?: string;
  objects?: {
    default?: {
      type: string;
      geometries?: Geometry[];
    };
  };
  features?: Geometry[];
  title?: string;
  transform?: {
    scale: number[];
    translate: number[];
  };
};

type Arc = [number, number];
type ArcArray = Arc[][];

type MapDataIndexCategories = {
  [key: string]: string;
};

export type MapDataIndex = {
  Default: MapDataIndexCategories;
  Custom: MapDataIndexCategories;
  Countries: MapDataIndexCategories;
  'Canada Provinces Admin 2': MapDataIndexCategories;
  'France Regions Admin 2': MapDataIndexCategories;
  'Germany Bundesländer Admin 2': MapDataIndexCategories;
  'Netherlands Provinces': MapDataIndexCategories;
  'Norway Counties': MapDataIndexCategories;
  'USA States': MapDataIndexCategories;
};
